import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { assignGuardrailsViolationProject, assignGuardrailsViolationWorkload, resolveViolation } from '../../../services/cloudopsApi';
import ProjectAssignmentSelect from '../../../components/ProjectAssignmentSelect';
import WorkloadAssignmentSelect from '../../../components/WorkloadAssignmentSelect';
import { useProject } from '../../../contexts/ProjectContext';

interface Violation {
  id: string;
  severity?: string;
  status?: string;
  message?: string;
  resource_data?: {
    project?: string;
    resource?: string;
    [key: string]: unknown;
  };
  assigned_to?: string;
  detected_at?: string;
  project_id?: string | null;
  workload_id?: string | null;
  workload_name?: string;
}

interface ViolationCardProps {
  violation: Violation;
  onResolve?: () => void;
}

const ViolationCard: React.FC<ViolationCardProps> = ({ violation, onResolve }) => {
  const navigate = useNavigate();
  const { projects } = useProject();
  const [currentViolation, setCurrentViolation] = useState(violation);

  useEffect(() => {
    setCurrentViolation(violation);
  }, [violation]);

  const getSeverityColor = (severity: string | undefined) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
        return 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300';
      case 'high':
        return 'bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-300';
      case 'medium':
        return 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300';
      case 'low':
        return 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
    }
  };

  const getStatusColor = (status: string | undefined) => {
    switch (status?.toLowerCase()) {
      case 'open':
        return 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300';
      case 'acknowledged':
        return 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300';
      case 'resolved':
        return 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300';
      case 'false_positive':
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
    }
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleResolve = async () => {
    try {
      await resolveViolation(currentViolation.id, 'Resolved via dashboard');
      setCurrentViolation((prev) => ({ ...prev, status: 'resolved' }));
      if (onResolve) onResolve();
    } catch (error: unknown) {
      console.error('Error resolving violation:', error);
      alert('Failed to resolve violation: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const handleProjectAssign = async (projectId: string | null) => {
    await assignGuardrailsViolationProject(currentViolation.id, projectId);
    setCurrentViolation((prev) => ({ ...prev, project_id: projectId }));
  };

  const projectName = currentViolation.project_id
    ? (projects.find((project) => project.id === currentViolation.project_id)?.name ?? null)
    : null;

  return (
    <Card hoverable>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getSeverityColor(currentViolation.severity)}`}>
                {currentViolation.severity?.toUpperCase() || 'UNKNOWN'}
              </span>
              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(currentViolation.status)}`}>
                {currentViolation.status?.toUpperCase() || 'UNKNOWN'}
              </span>
              <ProjectAssignmentSelect
                currentProjectId={currentViolation.project_id}
                onAssign={handleProjectAssign}
              />
              <WorkloadAssignmentSelect
                currentWorkloadId={currentViolation.workload_id}
                onAssign={async (workloadId) => {
                  await assignGuardrailsViolationWorkload(currentViolation.id, workloadId);
                  setCurrentViolation((prev) => ({ ...prev, workload_id: workloadId }));
                }}
              />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {currentViolation.message || 'Violation'}
            </h3>
            <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
              {(projectName || currentViolation.resource_data?.project) && (
                <p><span className="font-medium">Project:</span> {projectName || currentViolation.resource_data?.project}</p>
              )}
              {currentViolation.workload_name && (
                <p><span className="font-medium">Workload:</span> {currentViolation.workload_name}</p>
              )}
              {currentViolation.resource_data?.resource && (
                <p><span className="font-medium">Resource:</span> {currentViolation.resource_data.resource}</p>
              )}
              {currentViolation.assigned_to && (
                <p><span className="font-medium">Assigned to:</span> {currentViolation.assigned_to}</p>
              )}
              <p><span className="font-medium">Detected:</span> {formatDate(currentViolation.detected_at)}</p>
            </div>
          </div>
          <div className="ml-4 flex flex-col gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate(`/cloudops/guardrails/violations/${currentViolation.id}`)}
            >
              View Details
            </Button>
            {currentViolation.status === 'open' && (
              <Button
                variant="secondary"
                size="sm"
                onClick={handleResolve}
              >
                Resolve
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ViolationCard;
